/**
 * Perceived Price System
 * Handles dynamic price updates when variants change
 * Always shows: real_price (red) + fake_compare_price (strikethrough, 15% higher)
 */

(function () {
    'use strict';

    // ============================================
    // PRICE CALCULATION UTILITY
    // ============================================

    function calculateFakeComparePrice(realPrice) {
        return Math.round(realPrice * 1.15);
    }

    function formatPrice(price, currencyCodeEnabled) {
        // Convert cents to dollars
        const dollars = price / 100;

        // Use Shopify's money format if available
        if (window.Shopify && window.Shopify.formatMoney) {
            return window.Shopify.formatMoney(price);
        }

        // Fallback formatting
        const currency = window.Shopify?.currency?.active || 'USD';
        const formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2
        });

        return formatter.format(dollars);
    }

    // ============================================
    // UPDATE PRICE DISPLAY
    // ============================================

    function updatePriceDisplay(element, realPrice) {
        if (!element) return;

        const fakeComparePrice = calculateFakeComparePrice(realPrice);
        const currencyCodeEnabled = window.Shopify?.currency?.active || false;

        // Format prices
        const moneyRealPrice = formatPrice(realPrice, currencyCodeEnabled);
        const moneyFakeCompare = formatPrice(fakeComparePrice, currencyCodeEnabled);

        // Update data attributes
        element.setAttribute('data-price', realPrice);
        element.setAttribute('data-fake-compare', fakeComparePrice);

        // Find price elements
        const salePriceEl = element.querySelector('.price-item--sale, .perceived-price__sale');
        const comparePriceEl = element.querySelector('.price-item--regular, .perceived-price__compare');

        // Animate update
        if (salePriceEl) {
            salePriceEl.style.opacity = '0';
            salePriceEl.style.transition = 'opacity 0.2s ease';

            setTimeout(() => {
                salePriceEl.textContent = moneyRealPrice;
                salePriceEl.style.opacity = '1';
            }, 100);
        }

        if (comparePriceEl) {
            comparePriceEl.style.opacity = '0';
            comparePriceEl.style.transition = 'opacity 0.2s ease';

            setTimeout(() => {
                comparePriceEl.textContent = moneyFakeCompare;
                comparePriceEl.style.opacity = '1';
            }, 100);
        }
    }

    // ============================================
    // FETCH VARIANT PRICE
    // ============================================

    function fetchVariantPrice(variantId, callback) {
        if (!variantId) {
            if (callback) callback(null);
            return;
        }

        // Try to get from product data first
        const productData = window.Shopify?.analytics?.meta?.product;
        if (productData && productData.variants) {
            const variant = productData.variants.find(v => v.id === variantId);
            if (variant) {
                if (callback) callback(variant.price);
                return;
            }
        }

        // Fallback: fetch from product JSON
        const productHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];
        if (productHandle) {
            fetch(`/products/${productHandle}.js`)
                .then(response => response.json())
                .then(product => {
                    const variant = product.variants.find(v => v.id === variantId);
                    if (variant && callback) {
                        callback(variant.price);
                    }
                })
                .catch(() => {
                    if (callback) callback(null);
                });
        } else {
            if (callback) callback(null);
        }
    }

    // ============================================
    // UPDATE PRICE FROM VARIANT OBJECT
    // ============================================

    function updatePriceOnVariantChange(variant) {
        if (!variant || !variant.price) return;

        const price = variant.price;
        const fakeComparePrice = calculateFakeComparePrice(price);
        const currencyCodeEnabled = window.Shopify?.currency?.active || false;

        const moneyRealPrice = formatPrice(price, currencyCodeEnabled);
        const moneyFakeCompare = formatPrice(fakeComparePrice, currencyCodeEnabled);

        // Update main product price
        const mainPriceEl = document.querySelector('#price-' + (document.querySelector('[data-section]')?.dataset.section || '') + ' .price, .price--large');
        if (mainPriceEl) {
            const saleEl = mainPriceEl.querySelector('.price-item--sale');
            const compareEl = mainPriceEl.querySelector('.price-item--regular');

            if (saleEl) {
                saleEl.style.opacity = '0';
                setTimeout(() => {
                    saleEl.textContent = moneyRealPrice;
                    saleEl.style.opacity = '1';
                }, 100);
            }

            if (compareEl) {
                compareEl.style.opacity = '0';
                setTimeout(() => {
                    compareEl.textContent = moneyFakeCompare;
                    compareEl.style.opacity = '1';
                }, 100);
            }
        }

        // Update all perceived-price elements
        const perceivedPriceEls = document.querySelectorAll('.perceived-price');
        perceivedPriceEls.forEach(el => {
            const saleEl = el.querySelector('.perceived-price__sale');
            const compareEl = el.querySelector('.perceived-price__compare');

            if (saleEl) {
                saleEl.style.opacity = '0';
                setTimeout(() => {
                    saleEl.textContent = moneyRealPrice;
                    saleEl.style.opacity = '1';
                }, 100);
            }

            if (compareEl) {
                compareEl.style.opacity = '0';
                setTimeout(() => {
                    compareEl.textContent = moneyFakeCompare;
                    compareEl.style.opacity = '1';
                }, 100);
            }

            el.setAttribute('data-real-price', price);
            el.setAttribute('data-fake-compare', fakeComparePrice);
        });
    }

    // ============================================
    // UPDATE PRICES ON VARIANT CHANGE
    // ============================================

    function handleVariantChange(variantId) {
        fetchVariantPrice(variantId, (price) => {
            if (!price) return;

            // Update all price displays on the page
            const priceElements = document.querySelectorAll('.price[data-price], .perceived-price[data-real-price], .product-card-new__price');

            priceElements.forEach(element => {
                // Check if this element is for the changed variant
                const elementVariantId = element.closest('[data-variant-id]')?.dataset.variantId;
                if (elementVariantId && elementVariantId !== variantId.toString()) {
                    return; // Skip if not the changed variant
                }

                updatePriceDisplay(element, price);
            });

            // Update product card prices
            const productCards = document.querySelectorAll('.product-card-new');
            productCards.forEach(card => {
                const cardVariantId = card.querySelector('[data-variant-id]')?.dataset.variantId;
                if (cardVariantId === variantId.toString()) {
                    const priceEl = card.querySelector('.product-card-new__price');
                    if (priceEl) {
                        updatePriceDisplay(priceEl, price);
                    }
                }
            });
        });
    }

    // ============================================
    // INITIALIZE
    // ============================================

    function initPerceivedPriceSystem() {
        // Listen for variant changes from selects/inputs
        document.addEventListener('change', function (e) {
            const variantSelect = e.target.closest('select[name="id"], input[name="id"]');
            if (variantSelect) {
                const variantId = parseInt(variantSelect.value);
                if (variantId) {
                    handleVariantChange(variantId);
                }
            }
        });

        // Listen for Shopify product-info variant changes (PUB_SUB_EVENTS)
        if (window.PUB_SUB_EVENTS && typeof window.subscribe === 'function') {
            window.subscribe(window.PUB_SUB_EVENTS.variantChange, function (eventData) {
                if (eventData && eventData.data && eventData.data.variant) {
                    const variant = eventData.data.variant;
                    if (variant.price) {
                        updatePriceOnVariantChange(variant);
                    } else if (variant.id) {
                        handleVariantChange(variant.id);
                    }
                }
            });
        }

        // Listen for custom variant change events
        document.addEventListener('variant:change', function (e) {
            if (e.detail && e.detail.variant) {
                const variant = e.detail.variant;
                if (variant.price) {
                    updatePriceOnVariantChange(variant);
                } else if (variant.id) {
                    handleVariantChange(variant.id);
                }
            }
        });

        // Listen for product-info variant updates
        document.addEventListener('product:variant-change', function (e) {
            if (e.detail && e.detail.variant) {
                const variant = e.detail.variant;
                if (variant.price) {
                    updatePriceOnVariantChange(variant);
                } else if (variant.id) {
                    handleVariantChange(variant.id);
                }
            }
        });

        // Watch for price element updates (for product-info component)
        const priceObserver = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                if (mutation.type === 'childList' || mutation.type === 'characterData') {
                    const priceEl = mutation.target.closest('.price, #price-');
                    if (priceEl && priceEl.querySelector('.price-item--sale')) {
                        // Price was updated, check if we need to apply perceived discount
                        const saleEl = priceEl.querySelector('.price-item--sale');
                        const compareEl = priceEl.querySelector('.price-item--regular');

                        // Only update if compare price doesn't exist or is from Shopify (not our fake one)
                        if (saleEl && (!compareEl || !priceEl.hasAttribute('data-fake-compare'))) {
                            const priceText = saleEl.textContent.trim();
                            // Try to extract price value
                            const priceMatch = priceText.match(/[\d,]+\.?\d*/);
                            if (priceMatch) {
                                const priceStr = priceMatch[0].replace(/,/g, '');
                                const priceDollars = parseFloat(priceStr);
                                const priceCents = Math.round(priceDollars * 100);

                                // Update with perceived discount
                                updatePriceDisplay(priceEl, priceCents);
                            }
                        }
                    }
                }
            });
        });

        // Observe price containers
        const priceContainers = document.querySelectorAll('#price-, .price--large, .price');
        priceContainers.forEach(container => {
            priceObserver.observe(container, {
                childList: true,
                subtree: true,
                characterData: true
            });
        });

        // Update prices when color/size changes in product cards
        document.addEventListener('click', function (e) {
            const swatch = e.target.closest('.product-card-new__swatch');
            const sizeBtn = e.target.closest('.product-card-new__size-option-btn');

            if (swatch || sizeBtn) {
                const card = e.target.closest('.product-card-new');
                if (card) {
                    const variantId = swatch?.dataset.variantId || sizeBtn?.dataset.variantId;
                    if (variantId) {
                        setTimeout(() => {
                            handleVariantChange(parseInt(variantId));
                        }, 100);
                    }
                }
            }
        });
    }

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initPerceivedPriceSystem);
    } else {
        initPerceivedPriceSystem();
    }

    // Export for external use
    window.PerceivedPriceSystem = {
        updatePrice: updatePriceDisplay,
        calculateFakeCompare: calculateFakeComparePrice,
        formatPrice: formatPrice
    };
})();
